/* eslint-disable max-len */
/* eslint-disable complexity */
import { v4 as UUID } from 'uuid'
import { i18n } from 'next-i18next'
import format from 'date-fns/format'
import { AppState } from 'src/app/store'
import { ProjectApiPayload } from 'src/core/models'
import { loadDefaultContributionUnit } from 'src/core/json'
import {
  extractFormData,
  getFileFromBlobUrl,
  getSubstringSpaceReplaced
} from 'src/core/helpers'

export const projectApiPayloadSelector = (
  state: AppState
): ProjectApiPayload => {
  const form = state.projects.projectForm
  const projectToEdit = state.projects.projectDetails.project

  const projectCo2Balance = () => {
    if (form.is_vintage) {
      return 0
    }
    return form.volume_credits || 0
  }

  const getProjectCode = (): string => {
    if (projectToEdit) return projectToEdit.code
    return `${getSubstringSpaceReplaced(form.name).toUpperCase()}-${UUID()}`
  }

  return {
    isEdit: projectToEdit ? true : false,

    // DEFINITION
    name: form.name,
    kind: form.kind,
    is_active: form.is_active,

    // LOCATION
    country: form.country === 0 ? null : form.country || null,
    latitude: form.latitude,
    longitude: form.longitude,

    // CONTENT
    description: 'Non Empty Description',
    code: getProjectCode(),
    url: form.url,
    sdg_compliances: form.sdg_compliances,
    registered_since: form.registered_since
      ? format(
        new Date(form.registered_since),
        "yyyy-MM-dd'T'HH:mm:ss.SSSXXX"
      )
      : null,
    video: form.video,
    pictures: form.pictures,
    descriptions: [
      { language: 'en', text: form.description_en || '' },
      { language: 'es', text: form.description_es || '' },
      { language: 'fr', text: form.description_fr || '' },
      { language: 'ko', text: form.description_ko || '' },
      { language: 'pt', text: form.description_pt || '' },
      { language: 'de', text: form.description_de || '' },
      { language: 'it', text: form.description_it || '' },
      { language: 'pl', text: form.description_pl || '' },
      { language: 'zh', text: form.description_zh || '' }
    ],

    // PRICE and STOCK
    is_vintage: form.is_vintage,
    vintage_items: !projectToEdit
      ? form.vintage_items
      : form.vintage_items.map((vintage) => {
        const vint = projectToEdit.vintage_items.find(
          (v) => v.year === vintage.year
        )
        const deliveryDate = vintage.delivery_date
          ? format(
            new Date(vintage.delivery_date as string),
            "yyyy-MM-dd'T'HH:mm:ss.SSSXXX"
          )
          : null
        if (vint) {
          return {
            ...vintage,
            delivery_date: deliveryDate,
            integration_registry_vintage_id:
                              vint.integration_registry_vintage_id
          }
        } else {
          return {
            ...vintage,
            delivery_date: deliveryDate,
            integration_registry_vintage_id: null
          }
        }
      }),
    co2_balance: projectCo2Balance(),
    currency_token: form.currency_token,
    price: +form.price,
    is_free_donation: form.contributionProjectType === 'FREE_DONATION',
    contribution_unit: !form.contribution_unit
      ? null
      : loadDefaultContributionUnit(
        form.contribution_unit,
        i18n.language
      ),

    // CERTIFICATION
    stage: form.stage,
    status: form.status,
    type: form.type,
    sub_type: form.sub_type,
    methodology: form.methodology,
    mechanism: form.mechanism,
    energy_type: form.energy_type,
    central_name: form.central_name,
    standard_energy_type: form.standard_energy_type,
    credit_start: form.credit_start
      ? format(
        new Date(form.credit_start),
        "yyyy-MM-dd'T'HH:mm:ss.SSSXXX"
      )
      : null,
    credit_end: form.credit_end
      ? format(new Date(form.credit_end), "yyyy-MM-dd'T'HH:mm:ss.SSSXXX")
      : null,
    start_up_date_of_installation: form.start_up_date_of_installation
      ? format(
        new Date(form.start_up_date_of_installation),
        "yyyy-MM-dd'T'HH:mm:ss.SSSXXX"
      )
      : null,
    validator: form.validator,
    first_verifier: form.first_verifier,
    standard: form.standard,
    type_of_biome_ecosystem: form.type_of_biome_ecosystem,
    quantification_methodology: form.quantification_methodology,
    complementary_standards: form.complementary_standards,

    // DOCUMENTATION
    registry_depositing_id: form.registry_depositing_id,
    registry_depositing_name: form.registry_depositing_name,
    registry_depositing_url: form.registry_depositing_url,

    // TAX RELIEF
    is_tax_relief: form.is_tax_relief,
    tax_info: form.tax_info_en,
    tax_info_en: form.tax_info_en,
    tax_info_es: form.tax_info_es,
    tax_info_fr: form.tax_info_fr,
    tax_info_ko: form.tax_info_ko,
    tax_info_pt: form.tax_info_pt,
    tax_info_de: form.tax_info_de,
    tax_info_it: form.tax_info_it,
    tax_info_pl: form.tax_info_pl,
    tax_info_zh: form.tax_info_zh,

    // HEAD LINE
    headline:
            form.headline_en ||
            form.headline_es ||
            form.headline_fr ||
            form.headline_ko ||
            form.headline_pt ||
            form.headline_de ||
            form.headline_it ||
            form.headline_pl ||
            form.headline_zh,
    headline_en: form.headline_en,
    headline_es: form.headline_es,
    headline_fr: form.headline_fr,
    headline_ko: form.headline_ko,
    headline_pt: form.headline_pt,
    headline_de: form.headline_de,
    headline_it: form.headline_it,
    headline_pl: form.headline_pl,
    headline_zh: form.headline_zh,

    // CONFIGURATION
    is_property_of_climatetrade: form.is_property_of_climatetrade,
    billing_company_name: form.billing_company_name,
    billing_vat_number: form.billing_vat_number,
    billing_address: form.billing_address,
    billing_country: form.billing_country,
    billing_city: form.billing_city,
    billing_state: form.billing_state,
    billing_postal_code: form.billing_postal_code,

    //LAST MODIFIED
    last_updated: new Date().toISOString()
  }
}

export const projectApiFilesPayloadSelector = async (
  state: AppState
): Promise<FormData> => {
  const form = state.projects.projectForm

  try {
    const filesData = {
      main_image: form.main_image
        ? await getFileFromBlobUrl(form.main_image)
        : '',
      monitoring_report: form.monitoring_report
        ? await getFileFromBlobUrl(form.monitoring_report)
        : '',
      pdd: form.pdd ? await getFileFromBlobUrl(form.pdd) : '',
      validation_report: form.validation_report
        ? await getFileFromBlobUrl(form.validation_report)
        : '',
      verification_report: form.verification_report
        ? await getFileFromBlobUrl(form.verification_report)
        : '',
      credits_certificate: form.credits_certificate
        ? await getFileFromBlobUrl(form.credits_certificate)
        : '',
      device_registration_request: form.device_registration_request
        ? await getFileFromBlobUrl(form.device_registration_request)
        : '',
      resulting_from_the_issuer: form.resulting_from_the_issuer
        ? await getFileFromBlobUrl(form.resulting_from_the_issuer)
        : '',
      protocol_implementation_scheme: form.protocol_implementation_scheme
        ? await getFileFromBlobUrl(form.protocol_implementation_scheme)
        : '',
      executive_summary: form.executive_summary
        ? await getFileFromBlobUrl(form.executive_summary)
        : '',
      additional_documentation: form.additional_documentation
        ? await getFileFromBlobUrl(form.additional_documentation)
        : '',
      insurance_documentation: form.insurance_documentation
        ? await getFileFromBlobUrl(form.insurance_documentation)
        : ''
    }
    return extractFormData(filesData)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(error)
  }
}

export const projectImagesFilesSelector = async (
  state: AppState
): Promise<File[]> => {
  const pictures = state.projects.projectForm.pictures

  try {
    const files = pictures.map(
      async (picture) => await getFileFromBlobUrl(picture)
    )
    return Promise.all(files)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(error)
  }
}

export const newProjectImagesFilesSelector = async (
  state: AppState
): Promise<File[]> => {
  const pictures = state.projects.projectForm.pictures
  const projectToEdit = state.projects.projectDetails.project
  const images = pictures?.filter((picture) => {
    if (projectToEdit?.pictures?.find((pic) => pic?.image === picture))
      return false
    return true
  })

  try {
    const files = images.map(
      async (picture) => await getFileFromBlobUrl(picture)
    )
    return Promise.all(files)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(error)
  }
}
